import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
  deleteTickets,
  getTickets,
  pushTicketsBatch,
  TicketDetail,
  isDateInValidPeriod,
} from "../services/ticketApiService";
import { useFinancialPeriods } from "../hooks/useFinancialPeriods";
import { buildLinqFilter, buildLinqSort } from "../services/queryBuilder";
import {
  TableAction,
  TanStackDynamicTable,
} from "../components/TanStackDynamicTable";
import {
  ColumnDef,
  ColumnFiltering,
  ColumnFiltersState,
  SortingState,
} from "@tanstack/react-table";
import { Edit, Search, Send, Trash2, X } from "lucide-react";
import { formatTimestamp } from "../utils/formatters";
import { useNavigate } from "react-router-dom";
import { CustomSelect } from "../components/CustomSelect";
import { createPortal } from "react-dom";
import { TicketStats } from "../components/TicketStats";
import { useFilterParams } from "../hooks/useFilterParams";

const PushedPage: React.FC = () => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const financialPeriods = useFinancialPeriods();
  const { getInitialFilters, syncToUrl } = useFilterParams("pushed_v2_filters", (filters) => {
    setTableState((prev) => ({ ...prev, columnFilters: filters, pagination: { ...prev.pagination, pageIndex: 0 } }));
  });
  const [data, setData] = useState<TicketDetail[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [tableState, setTableState] = useState<{
    sorting: SortingState;
    pagination: any;
    columnFilters: ColumnFiltersState;
  }>({
    sorting: JSON.parse(localStorage.getItem(`pushed_v2_sorting`) || "[]"),
    pagination: { pageIndex: 0, pageSize: 100 },
    columnFilters: getInitialFilters(),
  });

  useEffect(() => {
    syncToUrl(tableState.columnFilters);
  }, [tableState.columnFilters]);

  const columns = useMemo<ColumnDef<TicketDetail>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <div className="px-1">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
              checked={table.getIsAllPageRowsSelected()}
              onChange={table.getToggleAllPageRowsSelectedHandler()}
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
              checked={row.getIsSelected()}
              disabled={!row.getCanSelect()}
              onChange={row.getToggleSelectedHandler()}
            />
          </div>
        ),
        size: 20,
      },
      {
        id: "ticketNumber",
        header: "Ticket #",
        accessorKey: "ticketNumber",
        meta: { type: "text" },
        cell: ({ row, getValue }) => {
          const ticketId = row.original.id; // Or whatever your internal DB ID property is
          const displayValue = getValue() as string;
          return (
            <Link
              to={`/ticket/${ticketId}`}
              className="text-primary hover:text-blue-600 dark:hover:text-blue-400 hover:underline font-bold transition-colors"
              // Stop propagation if clicking the link accidentally triggers row selection
              onClick={(e) => e.stopPropagation()}
            >
              {displayValue}
            </Link>
          );
        },
      },
      {
        id: "orderDate",
        header: "Order Date",
        accessorKey: "orderDate",
        meta: { type: "date" },
        filterFn: () => true,
        cell: ({ getValue }) => {
          const dateStr = getValue() as string | null;
          const invalid = !isDateInValidPeriod(dateStr, financialPeriods);
          return (
            <span className={invalid ? "px-1.5 py-0.5 rounded bg-red-50 border border-red-400 text-red-700 dark:bg-red-500/10 dark:border-red-500/50 dark:text-red-400" : ""}>
              {dateStr?.substring(0, 10) || ""}
            </span>
          );
        },
      },
      {
        id: "vendorID",
        header: "Supplier",
        accessorKey: "vendorID",
        meta: { type: "text" },
      },
      {
        id: "inventoryID",
        header: "Material",
        accessorKey: "inventoryID",
        meta: { type: "text" },
      },
      {
        id: "description",
        header: "Description",
        accessorKey: "description",
        meta: { type: "text" },
      },
      {
        id: "receivedQty",
        header: "Quantity",
        accessorKey: "receivedQty",
        meta: { type: "number" },
        filterFn: () => true,
      },
      { id: "uom", header: "UOM", accessorKey: "uom", meta: { type: "text" } },
      {
        id: "warehouse",
        header: "Plant",
        accessorKey: "warehouse",
        meta: { type: "text" },
      },
      {
        id: "siteid",
        header: "Quarry",
        accessorKey: "siteid",
        meta: { type: "text" },
      },
      {
        id: "branch",
        header: "Branch",
        accessorKey: "branch",
        meta: { type: "text" },
      },
      {
        id: "orderType",
        header: "Order Type",
        accessorKey: "orderType",
        meta: { type: "text" },
      },
      {
        id: "postPeriod",
        header: "Post Period",
        accessorKey: "postPeriod",
        meta: { type: "text" },
      },
      {
        id: "freightVendorID",
        header: "Hauler",
        accessorKey: "freightVendorID",
        meta: { type: "text" },
      },
      {
        id: "cgoProjectID",
        header: "CGO Project",
        accessorKey: "cgoProjectID",
        meta: { type: "text" },
      },
      {
        id: "blanketPurchaseOrder",
        header: "Blanket PO",
        accessorKey: "blanketPurchaseOrder",
        meta: { type: "text" },
      },
      {
        id: "truckID",
        header: "Truck",
        accessorKey: "truckID",
        meta: { type: "text" },
      },
      {
        id: "receivedTimestamp",
        header: "Received Timestamp",
        accessorKey: "receivedTimestamp",
        meta: { type: "datetime" },
        filterFn: () => true,
        cell: ({ getValue }) => {
          const rawValue = getValue() as string;
          return formatTimestamp(rawValue);
        },
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "status",
        meta: { type: "text" },
        cell: ({ getValue }) => {
          // 1. Grab the raw status string from the row
          const status = getValue() as string;

          // 2. Return your styled badge
          return (
            <span
              className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                status === "Pending"
                  ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400"
                  : status === "Pushed"
                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
                    : status === "Deleted"
                      ? "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400"
                      : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400"
              }`}
            >
              {status}
            </span>
          );
        },
      },
      {
        id: "batchId",
        header: "Batch",
        accessorKey: "batchId",
        meta: { type: "text" },
        cell: ({ getValue }) => {
          const rawValue = getValue() as string;
          return rawValue;
        },
      },
      {
        id: "pushedBy",
        header: "Pushed By",
        accessorKey: "pushedBy",
        meta: { type: "text" },
      },
      {
        id: "pushedTimestamp",
        header: "Pushed Timestamp",
        accessorKey: "pushedTimestamp",
        meta: { type: "timestamp" },
        filterFn: () => true,
        cell: ({ getValue }) => {
          const rawValue = getValue() as string;
          return formatTimestamp(rawValue);
        },
      },
    ],
    [financialPeriods],
  );

  const tableActions: TableAction[] = [
    {
      label: "View",
      icon: <Edit size={16} className="text-blue-500" />,
      onClick: (selectedRows) => {
        const ids = selectedRows.map((row) => row.id);

        if (ids.length === 0) return;

        const queueKey = `queue_${Date.now()}`;
        sessionStorage.setItem(queueKey, JSON.stringify({ queue: ids, index: 0, returnPath: "/pushed" }));
        window.open(`/ticket/${ids[0]}?queueKey=${queueKey}`, '_blank');
      },
    },
  ];

  const getCustomRowStyles = (row: TicketDetail) => {
    if (row.duplicatedTicketNumber) {
      return "!bg-red-50/50 dark:!bg-red-900/10 hover:!bg-red-50 dark:hover:!bg-red-900/30 !border-red-100 dark:!border-red-800/50 !text-red-800 dark:!text-red-200";
    }
    return "";
  };

  const fetchData = async () => {
    if (!accounts[0]) return;
    setIsLoading(true);

    const filter = buildLinqFilter(
      "Pushed",
      tableState.columnFilters,
    );
    const sort = buildLinqSort(tableState.sorting);
    const result = await getTickets(
      instance,
      accounts[0],
      filter,
      sort,
      tableState.pagination.pageIndex + 1,
      tableState.pagination.pageSize,
      "Completed",
    );

    setData(result.data);
    setTotalCount(result.totalCount);
    setIsLoading(false);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchData();
    }, 400);
    return () => {
      clearTimeout(handler);
    };
  }, [
    tableState.columnFilters,
    tableState.sorting,
    tableState.pagination,
  ]);

  return (
    <div className="p-6 h-full flex flex-col">
      <h1 className="text-xl font-bold mb-4 text-slate-900 dark:text-white">
        Pushed Tickets
      </h1>
      <TanStackDynamicTable
        data={data}
        columns={columns}
        rowCount={totalCount}
        tableName="pushed_v2"
        defaultPageSize={100}
        showPageSizeSelector={false}
        initialColumnFilters={tableState.columnFilters}
        onStateChange={setTableState}
        actions={tableActions}
        getRowClassName={getCustomRowStyles}
        renderHeaderStats={(selectedRows) => (
          <TicketStats selectedRows={selectedRows} totalCount={totalCount}></TicketStats>
        )}
      />
    </div>
  );
};

export default PushedPage;